import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { Star, Send, CheckCircle2, MessageSquareQuote, Menu } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import MobileMenu from '../components/MobileMenu';
import SiteFooter from '../components/SiteFooter';
import HreflangTags from '../components/HreflangTags';
import '../styles/HomePage.css';
import '../styles/RealReviewPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";

const COPY = {
  en: {
    tag: 'Real customer reviews',
    heading: 'Share your Registan Tourism experience',
    sub: 'Your review helps other travelers pick the right agency. It goes live instantly on our home page.',
    yourName: 'Your name',
    yourNamePh: 'e.g. Alisher M.',
    yourRating: 'Your rating',
    yourReview: 'Your review',
    yourReviewPh: 'What did we do well? Any advice for others?',
    instagram: 'Instagram (optional)',
    instagramPh: '@yourhandle',
    submit: 'Publish my review',
    submitting: 'Publishing…',
    successTitle: 'Review published — thank you!',
    successSub: 'Your review is now visible on our home page.',
    another: 'Leave another review',
    back: 'Back to home',
  },
  ru: {
    tag: 'Реальные отзывы клиентов',
    heading: 'Поделитесь опытом Registan Tourism',
    sub: 'Ваш отзыв помогает другим путешественникам выбрать правильное агентство. Он появится на нашей главной странице сразу.',
    yourName: 'Ваше имя',
    yourNamePh: 'например Алишер М.',
    yourRating: 'Оценка',
    yourReview: 'Ваш отзыв',
    yourReviewPh: 'Что вам понравилось? Есть ли советы для других?',
    instagram: 'Instagram (необязательно)',
    instagramPh: '@ваш_ник',
    submit: 'Опубликовать отзыв',
    submitting: 'Публикация…',
    successTitle: 'Отзыв опубликован — спасибо!',
    successSub: 'Ваш отзыв теперь виден на главной странице.',
    another: 'Оставить ещё один',
    back: 'На главную',
  },
  uz: {
    tag: 'Haqiqiy mijozlar sharhlari',
    heading: 'Registan Tourism tajribangizni ulashing',
    sub: "Sizning fikringiz boshqa sayohatchilarga to‘g‘ri agentlik tanlashda yordam beradi. U bosh sahifada darhol chiqadi.",
    yourName: 'Ismingiz',
    yourNamePh: 'masalan Alisher M.',
    yourRating: 'Baholang',
    yourReview: 'Sharhingiz',
    yourReviewPh: 'Bizning nima yaxshi qilganimizni yozing. Boshqalarga maslahat bering.',
    instagram: 'Instagram (ixtiyoriy)',
    instagramPh: '@sizning_ismingiz',
    submit: 'Sharhni chop etish',
    submitting: 'Chop etilmoqda…',
    successTitle: 'Sharh chop etildi — rahmat!',
    successSub: 'Sharhingiz endi bosh sahifada ko‘rinadi.',
    another: 'Yana sharh qoldirish',
    back: 'Bosh sahifaga',
  },
};

const RealReviewPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { theme } = useTheme();
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;
  const t = COPY[language] || COPY.en;

  const [form, setForm] = useState({ author: '', rating: 5, text: '', instagram: '' });
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    if (form.author.trim().length < 2) return setError(language === 'ru' ? 'Введите ваше имя' : language === 'uz' ? 'Ismingizni kiriting' : 'Please enter your name');
    if (form.text.trim().length < 8) return setError(language === 'ru' ? 'Напишите пару слов о вашем опыте' : language === 'uz' ? "Tajribangiz haqida bir necha so'z yozing" : 'Please share a few words about your experience');

    setSubmitting(true);
    try {
      const payload = {
        author: form.author.trim(),
        rating: form.rating,
        text: form.text.trim(),
        instagram: (form.instagram || '').trim() || undefined,
      };
      await axios.post(`${BACKEND_URL}/api/reviews/public`, payload);
      setSuccess(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || (language === 'ru' ? 'Не удалось отправить отзыв. Попробуйте позже.' : language === 'uz' ? 'Sharhni yuborib bo‘lmadi. Keyinroq urinib ko‘ring.' : 'Could not submit your review. Please try again.');
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm({ author: '', rating: 5, text: '', instagram: '' });
    setSuccess(false); setError('');
  };

  return (
    <div className="realreview-page" data-testid="realreview-page">
      <Helmet>
        <title>Leave a Review — Registan Tourism</title>
        <meta name="description" content={t.sub} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://registantours.com/realreview" />
      </Helmet>
      <HreflangTags path="/realreview" />

      <header className="dark-header">
        <div className="header-left">
          <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={currentLogo} alt="Registan Tourism" className="logo-img" />
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </header>
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      <main className="realreview-main">
        <div className="rr-shell">
          <div className="rr-badge">
            <MessageSquareQuote size={14} />
            <span>{t.tag}</span>
          </div>
          <h1 className="rr-heading" data-testid="realreview-h1">{t.heading}</h1>
          <p className="rr-sub">{t.sub}</p>

          {success ? (
            <div className="rr-success" data-testid="realreview-success">
              <div className="rr-success-badge">
                <CheckCircle2 size={22} />
              </div>
              <h2>{t.successTitle}</h2>
              <p>{t.successSub}</p>
              <div className="rr-success-actions">
                <button className="rr-btn rr-btn-secondary" onClick={reset} data-testid="realreview-another">
                  {t.another}
                </button>
                <Link className="rr-btn rr-btn-primary" to="/#reviews" data-testid="realreview-back">
                  {t.back}
                </Link>
              </div>
            </div>
          ) : (
            <form className="rr-form" onSubmit={submit} data-testid="realreview-form">
              <label className="rr-label">
                {t.yourName}
                <input
                  type="text"
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                  placeholder={t.yourNamePh}
                  maxLength={80}
                  required
                  data-testid="realreview-author"
                />
              </label>

              <div className="rr-label">
                <span>{t.yourRating}</span>
                <div className="rr-stars" data-testid="realreview-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      type="button"
                      key={n}
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      onClick={() => setForm({ ...form, rating: n })}
                      aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
                      data-testid={`realreview-star-${n}`}
                    >
                      <Star
                        size={30}
                        fill={n <= (hover || form.rating) ? '#F59E0B' : 'transparent'}
                        stroke={n <= (hover || form.rating) ? '#F59E0B' : '#94A3B8'}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <label className="rr-label">
                {t.yourReview}
                <textarea
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  placeholder={t.yourReviewPh}
                  rows={5}
                  maxLength={2000}
                  required
                  data-testid="realreview-text"
                />
                <span className="rr-count">{form.text.length} / 2000</span>
              </label>

              <label className="rr-label">
                {t.instagram}
                <input
                  type="text"
                  value={form.instagram}
                  onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                  placeholder={t.instagramPh}
                  maxLength={80}
                  data-testid="realreview-instagram"
                />
              </label>

              {error && <div className="rr-error" data-testid="realreview-error">{error}</div>}

              <button type="submit" className="rr-btn rr-btn-primary" disabled={submitting} data-testid="realreview-submit">
                <Send size={16} /> {submitting ? t.submitting : t.submit}
              </button>
            </form>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default RealReviewPage;
